import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { identify } from "../capture";
import VerificationCodeInput from "../components/VerificationCodeInput";
import { useWhitelabel } from "../context/WhitelabelContext";
import { useAuth } from "../context/AuthContext";
import {
  getInstanceConfig,
  getLogoMode,
  getSupportContactInfo,
  hostname,
  isSafeUrl,
  trackAffiliate,
} from "../utils";
import { useRecaptcha } from "../hooks/useRecaptcha";
import MfaSetup from "./MfaSetup";
import MfaVerify from "./MfaVerify";

const Login = ({
  mode,
  isLoginParam,
}: {
  mode: string;
  isLoginParam: boolean;
}) => {
  const [init, setInit] = useState(false);
  const [isLogin, setIsLogin] = useState(isLoginParam);
  const [authError, setAuthError] = useState<string>("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [resending, setResending] = useState(false);

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaNeedsSetup, setMfaNeedsSetup] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [tempUid, setTempUid] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { whitelabelLogos, whitelabelSettings, loaded } = useWhitelabel();
  const { currentUser, signIn, getToken } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { getRecaptchaToken } = useRecaptcha();

  // Check if this is a whitelabel domain (not the main platform)
  // Main platform returns isMainSite: true, whitelabel domains return isMainSite: false or undefined
  const isWhitelabelDomain = whitelabelSettings !== null && !whitelabelSettings.isMainSite;

  const getLoginLogo = () => {
    // Use whitelabel horizontal logo if available, otherwise use default logo
    // Use dark mode logo if in dark mode and available
    if (mode === 'dark' && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const handleMagicLinkLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setAuthError("Please enter your email address");
      return;
    }

    setLoading(true);
    setAuthError("");

    try {
      // Use backend-generated magic links for all domains (main site and whitelabel)
      const currentDomain = window.location.hostname;

      // Get reCAPTCHA token if enabled
      const recaptchaToken = await getRecaptchaToken("LOGIN");

      const response = await axios.post(`${hostname}/auth/magic-link/send`, {
        email: email,
        domain: currentDomain,
        recaptchaToken,
      });

      if (response.data.success) {
        setMagicLinkSent(true);
        setLoading(false);
      } else {
        throw new Error(response.data.error || "Failed to send magic link");
      }
    } catch (error: any) {
      console.error("Error sending magic link:", error);
      let errorMessage = "Failed to send magic link. Please try again.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (error.code === "auth/user-disabled") {
        errorMessage = `This account has been disabled. Please contact support at ${getSupportContactInfo(
          whitelabelSettings
        )}.`;
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many requests. Please wait before trying again.";
      }

      setAuthError(errorMessage);
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setAuthError("");

    try {
      // Get the current origin for the redirect URI
      const redirectUri = `${window.location.origin}/auth/google/callback`;

      // Get the Google OAuth URL from the backend
      const response = await axios.get(`${hostname}/auth/google/login`, {
        params: { redirect_uri: redirectUri }
      });

      if (response.data.authUrl) {
        // Redirect to Google OAuth
        window.location.href = response.data.authUrl;
      } else {
        throw new Error("Failed to get Google auth URL");
      }
    } catch (error: any) {
      console.error("Error initiating Google login:", error);
      setAuthError("Failed to initiate Google login. Please try again.");
      setLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (authError) {
      setAuthError("");
    }
  };

  const handleVerificationCode = async (code: string) => {
    setVerifyingCode(true);
    setCodeError("");

    try {
      const response = await axios.post(
        `${hostname}/auth/verification-code/verify`,
        {
          email: email,
          code: code,
        }
      );

      if (response.data.success) {
        const { customToken, uid, mfaRequired: needsMfa, mfaNeedsSetup: needsSetup } = response.data;

        // Check if MFA is required
        if (needsMfa) {
          setTempToken(customToken);
          setTempUid(uid);
          setMfaRequired(true);
          setMfaNeedsSetup(needsSetup || false);
          return;
        }

        // No MFA required, sign in directly
        signIn(customToken, { email, uid });

        // Navigate based on user state
        await handlePostLogin(customToken);
      } else {
        throw new Error(response.data.error || "Failed to verify code");
      }
    } catch (error: any) {
      console.error("Error verifying code:", error);
      let errorMessage = "Invalid verification code. Please try again.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setCodeError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleMfaComplete = async (token: string, uid?: string) => {
    // MFA completed, sign in with the token
    signIn(token, { email, uid: uid || tempUid });
    await handlePostLogin(token);
  };

  const handleMfaBack = () => {
    // Reset MFA state and go back to login
    setMfaRequired(false);
    setMfaNeedsSetup(false);
    setTempToken("");
    setTempUid("");
    setMagicLinkSent(false);
    setEmail("");
  };

  const handleResendMagicLink = async () => {
    setResending(true);
    setCodeError("");

    try {
      const currentDomain = window.location.hostname;

      // Get reCAPTCHA token if enabled
      const recaptchaToken = await getRecaptchaToken("LOGIN");

      const response = await axios.post(`${hostname}/auth/magic-link/send`, {
        email: email,
        domain: currentDomain,
        // Use backup email provider (SES) on resend for main platform only
        resend: !isWhitelabelDomain,
        recaptchaToken,
      });

      if (response.data.success) {
        // Reset the countdown
        setCanResend(false);
        setResendCountdown(60);
      } else {
        throw new Error(response.data.error || "Failed to resend magic link");
      }
    } catch (error: any) {
      console.error("Error resending magic link:", error);
      let errorMessage = "Failed to resend. Please try again.";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setCodeError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  // Countdown timer for resend button
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (magicLinkSent && resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
    } else if (magicLinkSent && resendCountdown === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [magicLinkSent, resendCountdown]);

  const handlePostLogin = async (token: string) => {
    try {
      // Fetch companies and usage in parallel
      const [companiesResponse, usageResponse] = await Promise.all([
        axios.get(`${hostname}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${hostname}/usage`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const hasCompanies = companiesResponse.data.companies && companiesResponse.data.companies.length > 0;
      const subscriptionType = usageResponse.data.usage?.subscriptionType || 'free';
      const isFree = subscriptionType.toLowerCase() === 'free' || subscriptionType === '';

      if (hasCompanies) {
        navigate("/dashboard/companies");
      } else if (whitelabelSettings?.disableFreeAccounts && !whitelabelSettings?.customSubscriptionEnabled && isFree) {
        // Whitelabel with free accounts disabled: must subscribe first
        navigate("/subscribe");
      } else {
        // Main platform shows the subscription page at the END of onboarding
        // (see Onboarding.tsx handleComplete), so new users go straight to onboarding.
        navigate("/onboarding");
      }
    } catch (error) {
      console.error("Error checking companies:", error);
      navigate("/dashboard/companies");
    }
  };

  useEffect(() => {
    // Only initialize after whitelabel settings have been loaded
    if (!init && loaded) {
      setInit(true);

      // If user is already logged in, redirect to dashboard
      if (currentUser) {
        getToken().then((token) => {
          if (token) {
            handlePostLogin(token);
          }
        });
        return;
      }

      // Check for email query parameter and pre-fill if present
      const emailParam = searchParams.get("email");
      if (emailParam) {
        const decodedEmail = decodeURIComponent(emailParam);
        setEmail(decodedEmail);

        // Auto-trigger magic link login after a short delay to ensure state is set
        setTimeout(async () => {
          if (!loading) {
            setLoading(true);
            setAuthError("");

            try {
              const currentDomain = window.location.hostname;
              const recaptchaToken = await getRecaptchaToken("LOGIN");

              const response = await axios.post(`${hostname}/auth/magic-link/send`, {
                email: decodedEmail,
                domain: currentDomain,
                recaptchaToken,
              });

              if (response.data.success) {
                setMagicLinkSent(true);
                setLoading(false);
              } else {
                throw new Error(response.data.error || "Failed to send magic link");
              }
            } catch (error: any) {
              console.error("Error sending magic link:", error);
              let errorMessage = "Failed to send magic link. Please try again.";

              if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
              }

              setAuthError(errorMessage);
              setLoading(false);
            }
          }
        }, 500);
      }
    }
  }, [loaded, whitelabelSettings, currentUser]);

  const renderCustomLogin = () => (
    <Grid container spacing={2} sx={{ p: 3 }}>
      <Grid item xs={12}>
        <Box
          sx={{
            p: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={getLoginLogo()}
            alt="logo"
            style={{
              objectFit: "contain",
              maxWidth: 200,
              maxHeight: 60,
              verticalAlign: "middle",
            }}
          />
        </Box>
      </Grid>

      {magicLinkSent ? (
        <>
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Check Your Email
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We've sent a verification code to <strong>{email}</strong>
            </Typography>
          </Grid>

          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Enter Verification Code
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the 6-digit code from your email
            </Typography>

            <VerificationCodeInput
              onComplete={handleVerificationCode}
              disabled={verifyingCode}
              error={!!codeError}
              helperText={codeError || (verifyingCode ? "Verifying..." : "")}
            />
          </Grid>

          <Grid item xs={12} style={{ textAlign: "center", marginTop: 20 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Or click the magic link in your email
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontStyle: "italic" }}
            >
              Didn't get an email? Check your spam folder.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={handleResendMagicLink}
                disabled={!canResend || resending}
              >
                {resending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : canResend ? (
                  "Resend Email"
                ) : (
                  `Resend in ${resendCountdown}s`
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setMagicLinkSent(false);
                  setEmail("");
                  setAuthError("");
                  setCodeError("");
                  setCanResend(false);
                  setResendCountdown(60);
                }}
              >
                Try Different Email
              </Button>
            </Box>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Typography variant="h4" gutterBottom>
              {isLogin ? "Login" : "Sign Up"}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {isLogin
                ? "Welcome back! Sign in to continue to your dashboard"
                : "Create your account to get started"}
            </Typography>
          </Grid>

          {authError && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setAuthError("")}>
                {authError}
              </Alert>
            </Grid>
          )}

          {/* Google Sign-In for main site only */}
          {!isWhitelabelDomain && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleLogin}
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 2,
                  textTransform: "none",
                  fontWeight: 500,
                }}
                startIcon={
                  <Box
                    component="img"
                    src={require("../img/google-logo.png")}
                    alt="Google logo"
                    sx={{ width: 20, height: 20 }}
                  />
                }
              >
                Continue with Google
              </Button>
            </Grid>
          )}

          {/* Divider for main site */}
          {!isWhitelabelDomain && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  or
                </Typography>
              </Divider>
            </Grid>
          )}

          <Grid item xs={12}>
            <form onSubmit={handleMagicLinkLogin}>
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                required
                autoComplete="email"
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !email.trim()}
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isLogin ? (
                  "Login with Email"
                ) : (
                  "Sign Up with Email"
                )}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, px: 4, textAlign: "center" }}>
                By proceeding, you acknowledge and accept our{" "}
                <Link
                  href={getInstanceConfig(mode, whitelabelSettings).termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </Link>
                {" "}and{" "}
                <Link
                  href={getInstanceConfig(mode, whitelabelSettings).privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </Typography>
            </form>
          </Grid>

          <Grid item xs={12} style={{ textAlign: "center", marginTop: 10 }}>
            {!isLogin ? (
              <Typography variant="body2">
                Already have an account?{" "}
                <Link
                  href="/login"
                >
                  Login
                </Link>
              </Typography>
            ) : (
              <Typography variant="body2">
                Don't have an account?{" "}
                <Link
                  href="/signup"
                >
                  Sign Up
                </Link>
              </Typography>
            )}
          </Grid>
        </>
      )}

      <Grid item xs={12} style={{ textAlign: "center", marginTop: 10 }}>
        <Typography variant="body2">
          <Link href="/login/sso">SSO Login</Link>
        </Typography>
      </Grid>
    </Grid>
  );

  // Always use custom login interface for both whitelabel and main site
  const loginContent = renderCustomLogin();

  // If MFA is required, show MFA components instead of login
  if (mfaRequired) {
    if (mfaNeedsSetup) {
      return (
        <MfaSetup
          tempToken={tempToken}
          email={email}
          uid={tempUid}
          onComplete={(token) => handleMfaComplete(token)}
        />
      );
    } else {
      return (
        <MfaVerify
          tempToken={tempToken}
          email={email}
          uid={tempUid}
          onComplete={handleMfaComplete}
          onBack={handleMfaBack}
        />
      );
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: isMobile ? "flex-start" : "center",
        minHeight: "100vh",
        width: "100vw",
        p: isMobile ? 2 : 0,
        pt: isMobile ? 4 : 0,
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            width: "100%",
            maxWidth: 450,
          }}
        >
          {loginContent}
        </Box>
      ) : (
        <Paper
          style={{
            width: 450,
          }}
        >
          {loginContent}
        </Paper>
      )}
    </Box>
  );
};

export default Login;
